import React, { useMemo } from "react";
import Moment from "moment";
import { t } from "../../../../i18n";
import { formatNumber } from "../../normalizer";
import CustomDataTable from "../../shared/CustomDataTable";

/**
 * Profile Balance Table component using CustomDataTable
 */
const BalanceTable = (props) => {
  const { currencyLabel, points, pagination, page, onPageChange } = props;

  // Check if any row has expired_date
  const hasExpiredDate = useMemo(() => {
    return points.some((p) => !!p.expired_date);
  }, [points]);

  // Column definitions - dynamically include expired_date if present
  const columns = useMemo(() => {
    const cols = [
      {
        key: "description",
        label: t("gamification2026.balance-table.description"),
        sortKey: "description",
      },
      {
        key: "date",
        label: t("gamification2026.balance-table.date"),
        sortKey: "date",
        render: (_v, item) => new Moment(item.date).format(t("date-format")),
      },
    ];

    if (hasExpiredDate) {
      cols.push({
        key: "expired_date",
        label: t("gamification2026.balance-table.expired_date"),
        sortKey: "expired_date",
        render: (_v, item) => (item.expired_date ? new Moment(item.expired_date).format(t("date-format")) : "-"),
      });
    }

    cols.push({
      key: "points_earned",
      label: currencyLabel,
      sortKey: "points_earned",
      render: (_v, item) => (item.points_earned > 0 ? `+${formatNumber(item.points_earned)}` : formatNumber(item.points_earned)),
    });

    return cols;
  }, [hasExpiredDate, currencyLabel]);

  // Sort function
  const sortFn = (data, sort) => {
    return [...data].sort((a, b) => {
      const multiplier = sort.direction === "asc" ? 1 : -1;
      if (sort.key === "date" || sort.key === "expired_date") {
        return (new Date(a[sort.key]) - new Date(b[sort.key])) * multiplier;
      }
      if (sort.key === "points_earned") {
        return (parseFloat(a.points_earned) - parseFloat(b.points_earned)) * multiplier;
      }
      if (sort.key === "description") {
        const descA = (a.description || "").toLowerCase();
        const descB = (b.description || "").toLowerCase();
        return descA.localeCompare(descB) * multiplier;
      }
      return 0;
    });
  };

  // Custom row renderer
  const renderRow = (item, index, className) => {
    return (
      <tr key={item.id || index} className={`data-table__row ${className}__row`}>
        <td className={`data-table__cell ${className}__cell ${className}__cell--description`}>
          {item.description}
        </td>
        <td className={`data-table__cell ${className}__cell ${className}__cell--date`}>
          {new Moment(item.date).format(t("date-format"))}
        </td>
        {hasExpiredDate && (
          <td className={`data-table__cell ${className}__cell ${className}__cell--expired_date`}>
            {item.expired_date ? new Moment(item.expired_date).format(t("date-format")) : "-"}
          </td>
        )}
        <td className={`data-table__cell ${className}__cell ${className}__cell--points_earned`}>
          {item.points_earned > 0 ? `+${formatNumber(item.points_earned)}` : formatNumber(item.points_earned)}
        </td>
      </tr>
    );
  };

  return (
    <CustomDataTable
      className="balance-table"
      columns={columns}
      data={points}
      renderRow={renderRow}
      rowKey={(item) => item.id}
      defaultSort={{ key: "date", direction: "desc" }}
      sortFn={sortFn}
      forceCards
      pagination={{
        totalPages: pagination.total_pages,
        currentPage: page,
        onPageChange,
      }}
      emptyMessage={t("gamification2026.balance-table.no-results")}
    />
  );
};

export default BalanceTable;
